import {Component, OnInit, ViewChild} from '@angular/core';
import {CrudSaveComponent} from "../_directives";
import {ActivatedRoute, Router} from "@angular/router";
import {Location} from "@angular/common";
import {AlertService, SalesPhraseService, StyleService} from "../_services";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {
  FunnelStepMobileIconButtonType,
  getFunnelStepMobileIconButtonTypeOptions,
  SalesPhrase,
  Style,
  StyleLanguage,
  StyleMediaType,
  StyleVariable,
  StyleLabelType,
  ItemStatusLabels,
} from '../_models';
import {takeUntil} from "rxjs/operators";
import {WebBuilderComponent} from '../web-builder';
import {NgxSmartModalService} from "ngx-smart-modal";
import {DEFAULT_THEME_FONT_FAMILY, getFontFamilies} from '../web-builder/editorConfig';

@Component({
  moduleId: module.id.toString(),
  templateUrl: './style-edit.component.html',
  styleUrls: ['./style.component.css', '../preview/preview-form.component.scss']
})
export class StyleNewComponent extends CrudSaveComponent implements OnInit {
  style: Style;
  salesPhrases: SalesPhrase[];
  styles = null;
  languageOptions = [
    {label: 'CSS', value: StyleLanguage.CSS} // we only support css for now - need to add compilers for scss and less
    //{label: 'SCSS', value: StyleLanguage.SCSS},
    //{label: 'Less', value: StyleLanguage.LESS}
  ];
  editorOptions = {
    theme: 'vs',
    language: 'css'
  };

  headerData = {
    style: '',
    enhanced_content: '',
  };
  footerData = {
    style: '',
    enhanced_content: '',
  };

  editors = {
    headerEditor: null,
    footerEditor: null,
    headerEditorOpen: false,
    footerEditorOpen: false,
  };

  editorLoaded: boolean = false;
  labelTypes = [
    {label: 'Subscription Status', value: StyleLabelType.SubscriptionStatus},
  ];
  labels = {
    [StyleLabelType.SubscriptionStatus]: ItemStatusLabels,
  }

  @ViewChild('headerEditor', { static: false }) headerBuilder: WebBuilderComponent;
  @ViewChild('footerEditor', { static: false }) footerBuilder: WebBuilderComponent;

  variableFields = [
    {label: 'Font Family', name: 'font_family', type: 'select', default: DEFAULT_THEME_FONT_FAMILY, options: getFontFamilies()},
    {label: 'Main Theme Color', name: 'theme_color', type: 'color'},
    {label: 'Button Hover Color', name: 'button_hover_color', type: 'color'},
    {label: 'Button Active Color', name: 'button_active_color', type: 'color'},
    {label: 'Button Shadow Color', name: 'button_shadow_color', type: 'color'},
    {label: 'Selection Background Color', name: 'selection_background_color', type: 'color'},
    {label: 'Action Button Color', name: 'action_color', type: 'color'},
    {label: 'Desktop Logo Height', name: 'desktop_logo_height', type: 'text'},
    {label: 'Desktop Logo Top Margin', name: 'desktop_logo_top_margin', type: 'text', default: 0},
    {label: 'Mobile Logo Height', name: 'mobile_logo_height', type: 'text'},
    {label: 'Mobile Logo Top Margin', name: 'mobile_logo_top_margin', type: 'text', default: 0},
    {label: 'Mobile Logo Bottom Margin', name: 'mobile_logo_bottom_margin', type: 'text', default: 0},
    {label: 'Mobile Icon Button Type', name: 'mobile_icon_button_type', type: 'select', default: FunnelStepMobileIconButtonType.TILE,  options: getFunnelStepMobileIconButtonTypeOptions()},
  ];

  previewData = null;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected styleService: StyleService,
    protected alertService: AlertService,
    protected salesPhraseService: SalesPhraseService,
    protected formBuilder: FormBuilder,
    protected modalService: NgxSmartModalService
  ) {
    super(router, location, route, styleService, alertService);
    this.title = 'Create Theme';
    this.objectName = 'theme';
    this.isNew = true;
    this.editorLoaded = true;
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      name: [null, Validators.required],
      language: [StyleLanguage.CSS, Validators.required],
      header_enhanced_mode: [false],
      footer_enhanced_mode: [false],
      header_enhanced_content: [''],
      footer_enhanced_content: [''],
      header_style: [''],
      footer_style: [''],
      content: [null],
      variables: this.formBuilder.array([]),
      labels: this.formBuilder.group({
        [StyleLabelType.SubscriptionStatus]: this.formBuilder.array([]),
      }),
    });

    this.variableFields.forEach(variable => {
      this.addVariable(variable.name, ('default' in variable) ? variable.default : null);
    });
    super.ngOnInit();

    this.salesPhraseService.list()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (salesPhrases: SalesPhrase[]) => {
          this.salesPhrases = salesPhrases;
        },
        error => {
          this.handleError(error);
        }
      );

    if (this.isNew) {
      this.styles = {};
    }
  }

  getVariableValue(index: number) {
    const fields = this.form.get('variables') as FormArray;
    const control = fields.at(index).get('value');

    return control.value;
  }

  setVariableValue(index: number, value: any) {
    const fields = this.form.get('variables') as FormArray;
    const control = fields.at(index);

    control.patchValue({value: value});
  }

  private addVariable(name: string = null, value: any = null) {
    let fields = this.form.get('variables') as FormArray;

    fields.push(this.createVariable({
      name: name,
      value: value
    } as StyleVariable));
  }

  private createVariable(field: StyleVariable) : FormGroup {
    return this.formBuilder.group({
      name: [field.name],
      value: [field.value]
    });
  }

  addLabel(type: StyleLabelType, name: string, value: string) {
    let fields = this.form.get('labels').get(type) as FormArray;
    fields.push(
      this.formBuilder.group({
        name: [name],
        value: [value]
      })
    );
  }

  removeLabel(type: StyleLabelType, index: number) {
    let fields = this.form.get('labels').get(type) as FormArray;
    fields.removeAt(index);
  }

  getLabelFields(type: StyleLabelType) {
    return (this.form.get('labels').get(type) as FormArray).controls;
  }

  getSalesPhraseStyle(phrase: SalesPhrase, style: string) {
    const selector = '.sales-phrase.' + phrase.slug;

    return this.getStyle(StyleMediaType.All, selector, style);
  }

  setSalesPhraseStyle(phrase: SalesPhrase, style: string, value: string) {
    const selector = '.sales-phrase.' + phrase.slug;

    this.setStyle(StyleMediaType.All, selector, style, value);
  }

  protected getStyle(mediaType: StyleMediaType, selector: string, style: string) {
    if (mediaType in this.styles) {
      if (selector in this.styles[mediaType]) {
        return this.styles[mediaType][selector][style] || null;
      }
    }

    return null;
  }

  protected setStyle(mediaType: StyleMediaType, selector: string, style: string, value: string) {
    if (!(mediaType in this.styles)) {
      this.styles[mediaType] = {};
    }

    if (!(selector in this.styles[mediaType])) {
      this.styles[mediaType][selector] = {};
    }

    this.styles[mediaType][selector][style] = value.trim();
  }

  protected getFormData() {
    let variables = {};
    let labels = {};

    //convert variables array to object
    if (this.form.value.variables && this.form.value.variables.length) {
      this.form.value.variables.forEach((field: StyleVariable) => {
        if (field.value !== null && field.value !== '') {
          variables[field.name] = field.value;
        }
      });
    }

    //convert labels array to object
    if (this.form.value.labels) {
      Object.entries(this.form.value.labels).forEach(([key, fields]) => {
        labels[key] = {};

        if (fields && Array.isArray(fields) && fields.length) {
          fields.forEach((field: StyleVariable) => {
            if (field.value !== null && field.value !== '') {
              labels[key][field.name] = field.value;
            }
          });
        }
      });
    }

    return Object.assign({}, this.form.value,
      {labels: labels},
      {variables: variables},
      {styles: this.styles});
  }

  enableEnhancedMode(event, isFooter = false) {
    if (isFooter) {
      this.form.get('footer_enhanced_mode').patchValue(event.target.checked);
    } else {
      this.form.get('header_enhanced_mode').patchValue(event.target.checked);
    }
  }

  toggleEditor(isFooter = false) {
    if (isFooter) {
      this.editors.footerEditorOpen = !this.editors.footerEditorOpen;
      this.editors.headerEditorOpen = false;
    } else {
      this.editors.headerEditorOpen = !this.editors.headerEditorOpen;
      this.editors.footerEditorOpen = false;
    }
  }

  saveStyle() {
    if (this.form.get('header_enhanced_mode').value && this.headerBuilder) {
      this.headerBuilder.saveBuilderContent();
    }
    if (this.form.get('footer_enhanced_mode').value && this.footerBuilder) {
      this.footerBuilder.saveBuilderContent();
    }
    this.onSubmit();
  }

  onSave(data, isFooter = false) {
    if (isFooter) {
      this.form.controls['footer_enhanced_content'].setValue(data.html);
      this.form.controls['footer_style'].setValue(data.css);
    } else {
      this.form.controls['header_enhanced_content'].setValue(data.html);
      this.form.controls['header_style'].setValue(data.css);
    }
  }

  preview() {
    this.previewData = this.getFormData();
    this.modalService.getModal('stylePreviewModal').open();
  }

  onPreview(previewUrl: string) {
    this.modalService.getModal('stylePreviewModal').close();
    window.open(previewUrl, '_blank');
  }

}
